import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Camera, Trash2, Plus, Clock, Bell, X, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

// ---------- CAMERA CAPTURE MODAL ----------
function CameraCaptureModal({ objectName, hint, onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facingMode }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (e) {
        setError('Kunde inte öppna kameran. Ge tillstånd i webbläsaren.');
      }
    };
    start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [facingMode]);

  const snap = () => {
    if (!videoRef.current || !canvasRef.current || busy) return;
    setBusy(true);
    const v = videoRef.current;
    const c = canvasRef.current;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    ctx.drawImage(v, 0, 0, w, h);
    c.toBlob((blob) => {
      if (!blob) { setBusy(false); return; }
      const reader = new FileReader();
      reader.onload = () => {
        onCapture(reader.result);
        setBusy(false);
      };
      reader.readAsDataURL(blob);
    }, 'image/jpeg', 0.82);
  };

  const flip = () => setFacingMode((m) => m === 'environment' ? 'user' : 'environment');

  return (
    <div className="fixed inset-0 z-[80] bg-black flex items-center justify-center p-4" data-testid="photo-alarm-camera">
      <div className="relative w-full max-w-2xl">
        <div className="absolute top-2 left-2 right-2 z-10 flex items-center justify-between">
          <div className="bg-white/90 rounded-2xl px-3 py-2 border-2 border-black shadow-[2px_2px_0_#000]">
            <p className="text-[10px] uppercase tracking-widest opacity-60">Mål</p>
            <p className="font-bold text-sm">📸 {objectName}</p>
            {hint && <p className="text-[10px] opacity-70">{hint}</p>}
          </div>
          <button onClick={onClose} className="neo-btn p-2 bg-white" data-testid="photo-alarm-camera-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <video ref={videoRef} playsInline muted
          className="w-full rounded-2xl border-4 border-white bg-black aspect-[4/3] object-cover" />
        <canvas ref={canvasRef} className="hidden" />
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-2xl">
            <div className="text-white text-center p-4 text-sm">{error}</div>
          </div>
        )}
        <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
          <button onClick={flip} className="neo-btn px-3 py-2 bg-white text-xs font-bold" data-testid="photo-alarm-flip">
            🔄 Byt
          </button>
          <button onClick={snap} disabled={!ready || busy}
            className="neo-btn w-20 h-20 rounded-full bg-white flex items-center justify-center disabled:opacity-50 shadow-[3px_3px_0_#000]"
            data-testid="photo-alarm-snap">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 border-4 border-white" />
          </button>
          <div className="w-16" />
        </div>
      </div>
    </div>
  );
}

// ---------- ALARM RUNNING (ringing) MODAL ----------
function AlarmRingingModal({ alarm, onDismissed, onCancel }) {
  const [phase, setPhase] = useState('ringing'); // ringing | camera | verifying | result
  const [result, setResult] = useState(null);

  const targetIds = alarm.object_ids?.length ? alarm.object_ids : (alarm.object_id ? [alarm.object_id] : []);
  const targetNames = (alarm.objects?.length ? alarm.objects : (alarm.object ? [alarm.object] : []))
    .map((o) => o?.name).filter(Boolean);
  const primaryHint = alarm.objects?.[0]?.hint || alarm.object?.hint || '';

  const handleCapture = async (dataUri) => {
    setPhase('verifying');
    try {
      const { data } = await axios.post(`${API}/photo-alarm/verify`, {
        object_ids: targetIds,
        image_base64: dataUri,
      }, { withCredentials: true });
      setResult(data);
      setPhase('result');
      if (data.match) {
        await axios.post(`${API}/photo-alarm/alarms/${alarm.id}/dismiss`, {}, { withCredentials: true }).catch(() => {});
      }
    } catch (e) {
      setResult({ match: false, reason: e.response?.data?.detail || 'Fel vid verifiering', celebration: '' });
      setPhase('result');
    }
  };

  if (phase === 'camera') {
    return <CameraCaptureModal
      objectName={targetNames.join(' ELLER ')}
      hint={primaryHint}
      onCapture={handleCapture}
      onClose={() => setPhase('ringing')} />;
  }

  return (
    <div className="fixed inset-0 z-[75] bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center p-4 animate-pulse-soft"
      data-testid="photo-alarm-ringing">
      <div className="bg-white rounded-3xl border-4 border-black shadow-[6px_6px_0_#000] max-w-md w-full p-6 text-center">
        {phase === 'ringing' && (
          <>
            <div className="text-6xl mb-3 animate-bounce">⏰</div>
            <p className="text-[10px] uppercase tracking-widest opacity-60">Väckarklocka</p>
            <h2 className="text-3xl font-bold font-['Fredoka']" data-testid="photo-alarm-label">{alarm.label}</h2>
            <p className="text-sm opacity-75 mt-2">
              För att stänga av: fotografera <b>{targetNames.length > 1 ? 'något av' : ''}</b>
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5 justify-center">
              {targetNames.map((n, i) => (
                <span key={i} className="px-2 py-1 rounded-full border-2 border-black bg-[#FFC8DD] text-xs font-bold">
                  📸 {n}
                </span>
              ))}
            </div>
            <button onClick={() => setPhase('camera')}
              className="neo-btn w-full mt-5 py-4 bg-gradient-to-r from-pink-400 to-rose-500 text-white font-bold text-lg flex items-center justify-center gap-2"
              data-testid="photo-alarm-open-camera">
              <Camera className="w-5 h-5" /> Öppna kameran
            </button>
            <button onClick={onCancel}
              className="text-xs opacity-60 mt-3 underline"
              data-testid="photo-alarm-cancel-ringing">
              Snooza 5 min
            </button>
          </>
        )}
        {phase === 'verifying' && (
          <>
            <div className="text-6xl mb-3">🔍</div>
            <h2 className="text-2xl font-bold font-['Fredoka']">Kollar bilden…</h2>
            <div className="mt-4 h-2 rounded-full bg-gray-200 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-pink-400 to-purple-500 animate-pulse w-3/4" />
            </div>
          </>
        )}
        {phase === 'result' && result && (
          <>
            <div className="text-6xl mb-3">{result.match ? '🎉' : '😅'}</div>
            <h2 className="text-2xl font-bold font-['Fredoka']">
              {result.match ? 'Godkänd!' : 'Försök igen'}
            </h2>
            {result.match ? (
              <>
                {result.matched_target && (
                  <p className="text-xs font-bold text-emerald-700 mt-1">✅ Hittat: {result.matched_target}</p>
                )}
                <p className="text-sm mt-2 italic">{result.celebration || 'Bra jobbat! Nu är du vaken 🌸'}</p>
                <p className="text-xs opacity-60 mt-1">{result.reason}</p>
                <button onClick={onDismissed}
                  className="neo-btn w-full mt-5 py-4 bg-gradient-to-r from-emerald-400 to-green-500 text-white font-bold text-lg flex items-center justify-center gap-2"
                  data-testid="photo-alarm-done">
                  <CheckCircle2 className="w-5 h-5" /> Släpp in dagen
                </button>
              </>
            ) : (
              <>
                <p className="text-sm mt-2">{result.reason}</p>
                <button onClick={() => setPhase('camera')}
                  className="neo-btn w-full mt-5 py-4 bg-gradient-to-r from-pink-400 to-rose-500 text-white font-bold text-lg flex items-center justify-center gap-2"
                  data-testid="photo-alarm-retry">
                  <Camera className="w-5 h-5" /> Ta en till bild
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------- MAIN ----------
export default function PhotoAlarm() {
  const [objects, setObjects] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showObjectForm, setShowObjectForm] = useState(false);
  const [showAlarmForm, setShowAlarmForm] = useState(false);
  const [editingAlarm, setEditingAlarm] = useState(null);
  const [ringingAlarm, setRingingAlarm] = useState(null);
  const [now, setNow] = useState(new Date());
  const dismissedRef = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, a] = await Promise.all([
        axios.get(`${API}/photo-alarm/objects`, { withCredentials: true }),
        axios.get(`${API}/photo-alarm/alarms`, { withCredentials: true }),
      ]);
      setObjects(o.data.objects || []);
      setAlarms(a.data.alarms || []);
    } catch (e) {
      toast.error('Kunde inte ladda');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Alarm engine: tick every 15s, trigger when time matches
  useEffect(() => {
    const check = () => {
      const d = new Date();
      setNow(d);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      const cur = `${hh}:${mm}`;
      const dow = (d.getDay() + 6) % 7; // Mon=0..Sun=6
      const key = `${d.toDateString()}_${cur}`;
      if (ringingAlarm) return;
      for (const a of alarms) {
        if (!a.enabled) continue;
        if (a.time !== cur) continue;
        if (a.days?.length && !a.days.includes(dow)) continue;
        if (dismissedRef.current[`${a.id}_${key}`]) continue;
        dismissedRef.current[`${a.id}_${key}`] = true;
        setRingingAlarm(a);
        break;
      }
    };
    check();
    const t = setInterval(check, 15000);
    return () => clearInterval(t);
  }, [alarms, ringingAlarm]);

  const removeObject = async (id) => {
    if (!window.confirm('Ta bort föremålet?')) return;
    try {
      await axios.delete(`${API}/photo-alarm/objects/${id}`, { withCredentials: true });
      toast.success('Borttaget');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte ta bort');
    }
  };
  const removeAlarm = async (id) => {
    if (!window.confirm('Ta bort alarmet?')) return;
    try {
      await axios.delete(`${API}/photo-alarm/alarms/${id}`, { withCredentials: true });
      toast.success('Borttaget');
      await load();
    } catch (e) {
      toast.error('Kunde inte ta bort');
    }
  };
  const toggleAlarm = async (a) => {
    try {
      await axios.patch(`${API}/photo-alarm/alarms/${a.id}`, {
        ...a, enabled: !a.enabled,
      }, { withCredentials: true });
      await load();
    } catch (e) {
      toast.error('Kunde inte uppdatera');
    }
  };

  const testAlarm = (a) => setRingingAlarm(a);

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar väckarklockan…</div>;

  return (
    <div className="space-y-5" data-testid="photo-alarm">
      <div className="neo-card p-5 bg-gradient-to-br from-[#FFE4E9] to-[#FDE8F5]">
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-full border-4 border-black bg-white flex items-center justify-center text-3xl shrink-0">
            📸
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold font-['Fredoka']">Foto-väckarklocka</h2>
            <p className="text-xs opacity-75 max-w-md mt-0.5">
              Alarmet slutar bara ringa när du fotograferar rätt föremål. Perfekt för dig som vill tvinga dig upp — ur sängen till tandborsten! 🪥⏰
            </p>
          </div>
        </div>
      </div>

      {/* Objects */}
      <div className="neo-card p-4 bg-white dark:bg-[#252545]">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold font-['Fredoka']">Mina föremål</p>
          <button onClick={() => setShowObjectForm(true)}
            className="neo-btn px-3 py-1.5 bg-[#BBF7D0] text-xs font-bold flex items-center gap-1"
            data-testid="photo-alarm-add-object-btn">
            <Plus className="w-3.5 h-3.5" /> Nytt föremål
          </button>
        </div>
        {objects.length === 0 ? (
          <p className="text-xs opacity-60 italic">Lägg till en tandborste, kaffekopp, tallrik eller något annat du vill fota på morgonen.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {objects.map((o) => (
              <div key={o.id} className="neo-card p-3 bg-[#FFFAF0] flex items-start gap-2" data-testid={`photo-alarm-object-${o.id}`}>
                <div className="text-2xl shrink-0">📸</div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm font-['Fredoka'] truncate">{o.name}</p>
                  {o.hint && <p className="text-[10px] opacity-70 line-clamp-2 italic">&quot;{o.hint}&quot;</p>}
                  {o.room && <p className="text-[10px] opacity-60">📍 {o.room}</p>}
                </div>
                <button onClick={() => removeObject(o.id)} className="p-1 opacity-60 hover:opacity-100">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alarms */}
      <div className="neo-card p-4 bg-white dark:bg-[#252545]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-bold font-['Fredoka']">Mina alarm</p>
            <p className="text-[10px] opacity-60">Just nu: {now.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          <button onClick={() => { setEditingAlarm(null); setShowAlarmForm(true); }}
            disabled={objects.length === 0}
            className="neo-btn px-3 py-1.5 bg-[#FFC8DD] text-xs font-bold flex items-center gap-1 disabled:opacity-50"
            data-testid="photo-alarm-add-alarm-btn">
            <Plus className="w-3.5 h-3.5" /> Nytt alarm
          </button>
        </div>
        {alarms.length === 0 ? (
          <p className="text-xs opacity-60 italic">
            {objects.length === 0 ? 'Lägg till ett föremål först.' : 'Inga alarm än. Tryck "Nytt alarm" 🌸'}
          </p>
        ) : (
          <div className="space-y-2">
            {alarms.map((a) => (
              <div key={a.id} className={`neo-card p-3 flex items-center gap-3 ${a.enabled ? 'bg-white' : 'bg-gray-100 opacity-70'}`}
                data-testid={`photo-alarm-alarm-${a.id}`}>
                <div className="text-3xl font-bold font-['Fredoka'] tabular-nums w-20">{a.time}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{a.label}</p>
                  <p className="text-[10px] opacity-70">
                    📸 {(a.objects?.length ? a.objects.map((o) => o.name).join(' · ') : a.object?.name) || '—'} · {a.days?.length ? a.days.map((d) => DAYS[d]).join(' ') : 'varje dag'}
                  </p>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={a.enabled} onChange={() => toggleAlarm(a)}
                      className="w-4 h-4 accent-pink-500"
                      data-testid={`photo-alarm-toggle-${a.id}`} />
                    <span className="text-[10px]">{a.enabled ? 'på' : 'av'}</span>
                  </label>
                  <div className="flex gap-1">
                    <button onClick={() => testAlarm(a)}
                      className="text-[10px] neo-btn px-2 py-0.5 bg-[#A2D2FF] font-bold"
                      title="Testa alarmet nu"
                      data-testid={`photo-alarm-test-${a.id}`}>
                      Testa
                    </button>
                    <button onClick={() => { setEditingAlarm(a); setShowAlarmForm(true); }} className="p-1 opacity-60 hover:opacity-100">
                      <Bell className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeAlarm(a.id)} className="p-1 opacity-60 hover:opacity-100">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showObjectForm && (
        <ObjectForm onSaved={async () => { await load(); setShowObjectForm(false); }}
          onClose={() => setShowObjectForm(false)} />
      )}
      {showAlarmForm && (
        <AlarmForm objects={objects} editing={editingAlarm}
          onSaved={async () => { await load(); setShowAlarmForm(false); setEditingAlarm(null); }}
          onClose={() => { setShowAlarmForm(false); setEditingAlarm(null); }} />
      )}
      {ringingAlarm && (
        <AlarmRingingModal alarm={ringingAlarm}
          onDismissed={() => setRingingAlarm(null)}
          onCancel={() => setRingingAlarm(null)} />
      )}
    </div>
  );
}

// ---------- FORMS ----------
function ObjectForm({ onSaved, onClose }) {
  const [name, setName] = useState('');
  const [hint, setHint] = useState('');
  const [room, setRoom] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error('Namn krävs'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/photo-alarm/objects`, { name, hint, room }, { withCredentials: true });
      toast.success('Sparat 📸');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte spara');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="neo-card bg-white max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold font-['Fredoka']">📸 Nytt föremål</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white"><X className="w-4 h-4" /></button>
        </div>
        <label className="block mb-2 text-xs font-bold">Vad ska du fota?
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="t.ex. min tandborste"
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white" maxLength={60}
            data-testid="photo-alarm-object-name" />
        </label>
        <label className="block mb-2 text-xs font-bold">Extra beskrivning (valfritt)
          <input value={hint} onChange={(e) => setHint(e.target.value)}
            placeholder="t.ex. blå med Batman på"
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white" maxLength={250} />
        </label>
        <label className="block mb-4 text-xs font-bold">Rum (valfritt)
          <input value={room} onChange={(e) => setRoom(e.target.value)}
            placeholder="badrum"
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white" maxLength={40} />
        </label>
        <button onClick={save} disabled={saving}
          className="neo-btn w-full py-3 bg-[#BBF7D0] font-bold disabled:opacity-50"
          data-testid="photo-alarm-object-save">
          {saving ? 'Sparar…' : 'Spara'}
        </button>
      </div>
    </div>
  );
}

function AlarmForm({ objects, editing, onSaved, onClose }) {
  const [label, setLabel] = useState(editing?.label || 'Vakna');
  const [time, setTime] = useState(editing?.time || '07:00');
  const [days, setDays] = useState(editing?.days || []);
  const [objectIds, setObjectIds] = useState(() => {
    if (editing?.object_ids?.length) return editing.object_ids;
    if (editing?.object_id) return [editing.object_id];
    return objects[0] ? [objects[0].id] : [];
  });
  const [enabled, setEnabled] = useState(editing?.enabled !== false);
  const [saving, setSaving] = useState(false);

  const toggleDay = (d) => setDays((arr) => arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d].sort());
  const toggleObject = (id) => setObjectIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const save = async () => {
    if (!objectIds.length) { toast.error('Välj minst ett föremål'); return; }
    setSaving(true);
    try {
      const body = { label, time, days, object_ids: objectIds, enabled, snooze_minutes: 5 };
      if (editing) {
        await axios.patch(`${API}/photo-alarm/alarms/${editing.id}`, body, { withCredentials: true });
      } else {
        await axios.post(`${API}/photo-alarm/alarms`, body, { withCredentials: true });
      }
      toast.success('Sparat ⏰');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte spara');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="neo-card bg-white max-w-md w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold font-['Fredoka']">⏰ {editing ? 'Redigera alarm' : 'Nytt alarm'}</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white"><X className="w-4 h-4" /></button>
        </div>
        <label className="block mb-3 text-xs font-bold">Namn
          <input value={label} onChange={(e) => setLabel(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white" maxLength={60}
            data-testid="photo-alarm-form-label" />
        </label>
        <label className="block mb-3 text-xs font-bold">Tid
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white text-xl font-bold tabular-nums"
            data-testid="photo-alarm-form-time" />
        </label>
        <div className="mb-3">
          <p className="text-xs font-bold mb-1">Dagar (tomt = varje dag)</p>
          <div className="flex gap-1 flex-wrap">
            {DAYS.map((d, i) => (
              <button key={i} type="button" onClick={() => toggleDay(i)}
                className={`w-10 h-10 rounded-full border-2 border-black text-xs font-bold ${days.includes(i) ? 'bg-[#FFC8DD] scale-105' : 'bg-white'}`}>
                {d}
              </button>
            ))}
          </div>
        </div>
        <div className="mb-3">
          <p className="text-xs font-bold mb-1">Fota vad? (välj ett eller flera — vilket som helst räcker)</p>
          <div className="flex flex-wrap gap-2">
            {objects.map((o) => (
              <button key={o.id} type="button" onClick={() => toggleObject(o.id)}
                className={`px-3 py-1.5 rounded-full border-2 border-black text-xs font-bold flex items-center gap-1 ${objectIds.includes(o.id) ? 'bg-[#FFC8DD] scale-105' : 'bg-white'}`}
                data-testid={`photo-alarm-form-obj-${o.id}`}>
                📸 {o.name}{o.room ? ` · ${o.room}` : ''}
              </button>
            ))}
          </div>
          {objectIds.length > 1 && (
            <p className="text-[10px] opacity-70 mt-1 italic">Multi-mål: alarmet stängs av så fort du hittar och fotar ett av dem 🎲</p>
          )}
        </div>
        <label className="flex items-center gap-2 mb-4 text-xs font-bold">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 accent-pink-500" />
          Aktivt
        </label>
        <button onClick={save} disabled={saving}
          className="neo-btn w-full py-3 bg-[#BBF7D0] font-bold disabled:opacity-50"
          data-testid="photo-alarm-form-save">
          {saving ? 'Sparar…' : 'Spara alarm'}
        </button>
      </div>
    </div>
  );
}
